<template>
	<BlockVisibilityObserver title="MAIN">
		<section class="kova-lp-usa">
			<!-- Mobile only. Positioned, so every block below is positioned too to paint over it. -->
			<img alt="" class="kova-lp-usa__flag" :src="flag" />

			<!-- The logo opens the white panel on mobile; on desktop it is the header bar with its
			     own CTA, on the same white as the hero below it. -->
			<header class="kova-lp-usa__bar">
				<img alt="Kova" class="kova-lp-usa__logo" :src="logo" />

				<button class="kova-lp-usa__bar-cta" type="button" @click="onHeaderCtaClick">{{ headerCtaLabel }}</button>
			</header>

			<div class="kova-lp-usa__inner">
				<div class="kova-lp-usa__copy">
					<div class="kova-lp-usa__rating">
						<div class="kova-lp-usa__rating-pill">
							<div class="kova-lp-usa__rating-score">
								<LandingKovaStars class="kova-lp-usa__stars" tone="light" />
								<p class="kova-lp-usa__rating-value">{{ rating.score }}&nbsp; {{ rating.label }}</p>
							</div>
							<p class="kova-lp-usa__rating-caption">{{ rating.caption }}</p>

							<!-- Mobile placement; on desktop the badge moves onto the product card. -->
							<img alt="" class="kova-lp-usa__badge kova-lp-usa__badge_place_rating" :src="badge" />
						</div>
					</div>

					<!-- v-text, not an interpolation: the runs carry their own trailing spaces
					     and Vue's condense mode would add one more around a wrapped `{{ }}`. -->
					<h1 class="kova-lp-usa__title">
						<span
							v-for="(part, index) in title"
							:key="index"
							:class="{ 'kova-lp-usa__title_accent': part.accent }"
							v-text="part.text"
						/>
					</h1>

					<p class="kova-lp-usa__description">{{ description }}</p>

					<!-- Desktop placement of the price; on mobile it sits inside the product card. -->
					<div class="kova-lp-usa__price kova-lp-usa__price_place_copy">
						<p class="kova-lp-usa__price-label">{{ price.label }}</p>
						<div class="kova-lp-usa__price-row">
							<p class="kova-lp-usa__price-now">{{ price.now }}</p>
							<div class="kova-lp-usa__price-aside">
								<p class="kova-lp-usa__price-was">{{ price.was }}</p>
								<p class="kova-lp-usa__price-period">{{ price.period }}</p>
							</div>
						</div>
					</div>
				</div>

				<!-- One card, two compositions: mobile lists the product with its price,
				     desktop shows the shot under the badge. The chips end both. -->
				<div class="kova-lp-usa__card">
					<img alt="" class="kova-lp-usa__badge kova-lp-usa__badge_place_card" :src="badge" />

					<img alt="" class="kova-lp-usa__shot" :src="product.image" />

					<div class="kova-lp-usa__product">
						<div class="kova-lp-usa__thumb">
							<img alt="" class="kova-lp-usa__thumb-image" :src="product.thumb" />
						</div>

						<div class="kova-lp-usa__product-copy">
							<p class="kova-lp-usa__product-name">{{ product.name }}</p>

							<div class="kova-lp-usa__price kova-lp-usa__price_place_card">
								<p class="kova-lp-usa__price-label">{{ price.label }}</p>
								<div class="kova-lp-usa__price-row">
									<p class="kova-lp-usa__price-now">{{ price.now }}</p>
									<div class="kova-lp-usa__price-aside">
										<p class="kova-lp-usa__price-was">{{ price.was }}</p>
										<p class="kova-lp-usa__price-period">{{ price.period }}</p>
									</div>
								</div>
							</div>
						</div>
					</div>

					<ul class="kova-lp-usa__chips">
						<li v-for="feature in features" :key="feature" class="kova-lp-usa__chip">
							<InlineSvg class="kova-lp-usa__chip-check" :src="checkIcon" />
							<span>{{ feature }}</span>
						</li>
					</ul>
				</div>

				<div class="kova-lp-usa__actions">
					<button class="kova-lp-usa__cta" type="button" @click="onCtaClick">
						<span>{{ ctaLabel }}</span>
						<InlineSvg class="kova-lp-usa__cta-arrow" :src="arrowIcon" />
					</button>

					<div class="kova-lp-usa__promises">
						<p v-for="promise in promises" :key="promise.label" class="kova-lp-usa__promise">
							<span v-if="promise.icon === 'cross'" class="kova-lp-usa__promise-ring">
								<InlineSvg class="kova-lp-usa__cross" :src="crossIcon" />
							</span>
							<InlineSvg v-else class="kova-lp-usa__promise-check" :src="checkIcon" />
							<span>{{ promise.label }}</span>
						</p>
					</div>
				</div>
			</div>
		</section>

		<!-- divider (6346:46418) / lines (6202:57994) — the certification ticker on the page,
		     below the panel. Both frames run the row past the edges, so it reads as a marquee. -->
		<LandingKovaMarquee class="kova-lp-usa__marquee">
			<template v-for="item in trust" :key="item.label">
				<span class="kova-lp-usa__trust">
					<img alt="" class="kova-lp-usa__trust-icon" :src="trustIcons[item.icon]" />
					<span>{{ item.label }}</span>
				</span>
				<span aria-hidden="true" class="kova-lp-usa__trust-dot">·</span>
			</template>
		</LandingKovaMarquee>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import { useOnboardingStepEvents } from '@/composables/useOnboardingStepEvents'
import LandingKovaMarquee from '@/modules/Landing/components/LandingKovaMarquee.vue'
import LandingKovaStars from '@/modules/Landing/components/LandingKovaStars.vue'

import arrowIcon from '@/assets/icons/ds_arrow_right.svg'
import checkIcon from '@/assets/kova/landing/check.svg'
import crossIcon from '@/assets/kova/landing/cross.svg'
import trustClinicians from '@/assets/kova/landing/trust-clinicians.svg'
import trustEligibility from '@/assets/kova/landing/trust-eligibility.svg'
import trustHipaa from '@/assets/kova/landing/trust-hipaa.svg'
import trustLegitscript from '@/assets/kova/landing/trust-legitscript.svg'
import badge from '@/assets/kova/landing/usa/badge-made-in-usa.png'
import flag from '@/assets/kova/landing/usa/hero-flag.png'
import logo from '@/assets/kova/landing/usa/logo-kova-navy.svg'

const trustIcons = {
	clinicians: trustClinicians,
	eligibility: trustEligibility,
	hipaa: trustHipaa,
	legitscript: trustLegitscript,
}

// First screen of the USA creative (DEV-35942) — mobile Figma 6346:46332 + 6346:46418,
// desktop 6202:57891. It replaces the header as well: on mobile the logo opens the panel, on
// desktop the header bar is part of this block.
export default {
	name: 'LandingKovaHeroUsa',

	components: { BlockVisibilityObserver, LandingKovaMarquee, LandingKovaStars },

	props: {
		ctaLabel: {
			type: String,
			required: true,
		},

		description: {
			type: String,
			required: true,
		},

		// Feature chips at the bottom of the product card — 5 in both frames.
		features: {
			type: Array,
			required: true,
		},

		/** Desktop header CTA. */
		headerCtaLabel: {
			type: String,
			required: true,
		},

		// { label, now, was, period }
		price: {
			type: Object,
			required: true,
		},

		// { name, thumb, image } — `thumb` is the vial in the mobile card's tile, `image` the
		// desktop card's shot. Both are cut-outs with their shadows baked in.
		product: {
			type: Object,
			required: true,
		},

		// [{ icon: 'cross' | 'check', label }] — the pair next to the CTA.
		promises: {
			type: Array,
			required: true,
		},

		// { score, label, caption }
		rating: {
			type: Object,
			required: true,
		},

		// [{ text, accent? }]
		title: {
			type: Array,
			required: true,
		},

		// [{ icon: keyof trustIcons, label }] — the certification marquee.
		trust: {
			type: Array,
			required: true,
		},
	},

	emits: ['progressLoaderCompleted'],

	setup() {
		const { jetButtonClick } = useOnboardingStepEvents()
		return { arrowIcon, badge, checkIcon, crossIcon, flag, jetButtonClick, logo, trustIcons }
	},

	methods: {
		onCtaClick() {
			// Mapped to block MAIN by LANDING_CTA_BLOCK_NAMES in LandingView.
			this.$emit('progressLoaderCompleted', 'main_eligible')
		},

		onHeaderCtaClick() {
			// Same contract as LandingKovaHeader: the header is not a tracked block (AL-12446
			// #99131), so the click goes out without a block_name instead of naming one that
			// can never be joined.
			this.jetButtonClick('CHECK_MY_ELIGIBILITY')
			this.$emit('progressLoaderCompleted', 'header_eligible')
		},
	},
}
</script>

<style lang="scss" scoped>
// Mobile (6346:46332): one white panel with rounded bottom corners — logo, title, rating,
// copy, product card, CTA — on the page colour, with the flag in its top-left corner.
.kova-lp-usa {
	position: relative;
	overflow: hidden;

	padding: var(--size-36) var(--size-16) var(--size-32);

	font-family: var(--kova-lp-font);
	border-end-start-radius: var(--size-32);
	border-end-end-radius: var(--size-32);
	background: var(--kova-lp-surface);
}

.kova-lp-usa__flag {
	position: absolute;
	left: 0;
	top: 0;
	// 132x111 (6346:46417), in rem so it shrinks with the rest of the panel on small phones,
	// where the root drops to 14px (style.scss).
	width: 8.25rem;
	height: auto;
}

.kova-lp-usa__bar {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	height: var(--size-24);
	padding-inline: var(--size-24);
}

// One vector for both breakpoints. The mobile frame draws it 106 wide over a 104x24 box
// (6346:46335), so it keeps that width and overflows the 24px row evenly — the row centres
// it. rem for the small-phone root.
.kova-lp-usa__logo {
	display: block;
	width: 6.625rem; // 106px
	height: auto;
}

.kova-lp-usa__bar-cta {
	display: none; // desktop only
}

// Mobile reorders the column: the title follows the logo, the rating comes after it.
.kova-lp-usa__copy {
	position: relative;
	display: flex;
	flex-direction: column;
	margin-top: var(--size-16);
}

// The frame sets the headline in a 358px box (6346:46348), which breaks it into the three
// lines of the design; a wider phone would pull "without" up to the first line. The cap is in
// em so the breaks stay the same while the font shrinks with the small-phone root.
.kova-lp-usa__title {
	order: -1;
	max-width: calc(358em / 28);
	margin: 0 auto;

	font-size: var(--size-28);
	font-weight: 600;
	// 1.12 in Figma, which lays text out on whole-pixel lines: 3 x 31 = the 93px node (6346:46348).
	line-height: 1.9375rem; // 31px, in rem to follow the font on small phones
	letter-spacing: -0.56px;

	color: var(--kova-lp-text);
	text-align: center;
}

.kova-lp-usa__title_accent {
	color: var(--kova-lp-highlight);
}

// The wrapper keeps 12px clear on the right so the sticker can hang 12px past the pill,
// and 16px below for the part of it that drops under the pill.
.kova-lp-usa__rating {
	margin-top: var(--size-24);
	padding: 0 var(--size-12) var(--size-16) 0;
}

.kova-lp-usa__rating-pill {
	position: relative;

	display: flex;
	flex-direction: column;
	gap: var(--size-2);
	padding: var(--size-8) var(--size-16);

	border-radius: var(--size-16);
	color: var(--kova-lp-hero-rating-ink);
	background: var(--kova-lp-hero-rating);

	// The mobile row (6346:46353) dims the last star's tail less than the desktop one.
	--kova-lp-stars-tail-opacity: 0.55;
}

.kova-lp-usa__rating-score {
	display: flex;
	align-items: center;
	gap: var(--size-8);
}

.kova-lp-usa__stars {
	height: 0.9375rem; // 15px: 5 stars + 4 x 2px gaps = the 88px row from Figma 6346:46353
}

.kova-lp-usa__rating-value,
.kova-lp-usa__rating-caption {
	margin: 0;

	font-size: var(--size-14);
	line-height: var(--size-20); // 1.4, which Figma rounds to 20 here (6346:46352)
}

.kova-lp-usa__rating-value {
	font-weight: 600;
	white-space: nowrap;
}

.kova-lp-usa__badge {
	display: block;
	border-radius: 50%;
}

.kova-lp-usa__badge_place_rating {
	position: absolute;
	right: calc(-1 * var(--size-12));
	top: 50%;
	width: var(--size-88);
	height: var(--size-88);

	transform: translateY(-50%);
}

.kova-lp-usa__badge_place_card {
	display: none; // desktop only
}

.kova-lp-usa__description {
	position: relative;
	margin: var(--size-20) 0 0;

	font-size: var(--size-16);
	line-height: var(--size-22);
	letter-spacing: -0.064px;

	color: var(--kova-lp-text);
}

.kova-lp-usa__card {
	position: relative;

	display: flex;
	flex-direction: column;
	gap: var(--size-16);
	margin-top: var(--size-16);
	padding: var(--size-20);

	border-radius: var(--size-24);
	background: var(--kova-lp-hero-card);
}

.kova-lp-usa__shot {
	display: none; // desktop only
}

.kova-lp-usa__product {
	display: flex;
	align-items: end;
	gap: var(--size-16);
}

// The tile is as tall as the copy next to it and crops the vial's foot (6346:46374).
.kova-lp-usa__thumb {
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
	align-self: stretch;
	width: var(--size-84);

	border-radius: var(--size-16);
	background: var(--kova-lp-surface);
}

.kova-lp-usa__thumb-image {
	position: absolute;
	left: var(--size-12);
	top: var(--size-4);
	width: var(--size-60); // 60x102, the cut-out keeps its ratio
	height: auto;
}

.kova-lp-usa__product-copy {
	flex: 1;
	min-width: 0;

	display: flex;
	flex-direction: column;
	gap: var(--size-4);
}

// SF Pro in the frame, as on the product cards below; Inter stands in for it there too.
.kova-lp-usa__product-name {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-20);
	letter-spacing: -0.32px;

	color: var(--kova-lp-text);
}

.kova-lp-usa__price {
	display: flex;
	flex-direction: column;
	gap: var(--size-4);
}

// After the base rule, which would otherwise show it again.
.kova-lp-usa__price_place_copy {
	display: none; // desktop only — mobile prices the product inside the card
}

.kova-lp-usa__price-label,
.kova-lp-usa__price-was,
.kova-lp-usa__price-period {
	margin: 0;

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-16);
	letter-spacing: -0.28px;
}

.kova-lp-usa__price-label {
	color: var(--kova-lp-text-soft);
}

.kova-lp-usa__price-row {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	padding-block: var(--size-4);
}

.kova-lp-usa__price-now {
	margin: 0;

	font-size: var(--size-40);
	font-weight: 600;
	// Figma trims this box to the cap height, 28px at 40px Poppins. Matching line-height
	// reproduces it without relying on `text-box`, still Chromium/WebKit-only.
	line-height: var(--size-28);
	letter-spacing: -0.8px;

	color: var(--kova-lp-highlight);
}

.kova-lp-usa__price-aside {
	display: flex;
	flex-direction: column;
	gap: 1px;
}

.kova-lp-usa__price-was {
	color: var(--Text-Primary-60);
	text-decoration: line-through;
}

.kova-lp-usa__price-period {
	color: var(--kova-lp-highlight);
}

.kova-lp-usa__chips {
	display: flex;
	flex-wrap: wrap;
	gap: var(--size-4);
	margin: 0;
	padding: 0;

	list-style: none;
}

.kova-lp-usa__chip {
	display: flex;
	align-items: center;
	gap: var(--size-6);
	padding: var(--size-8) var(--size-12) var(--size-8) var(--size-8);

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20); // 1.4, rounded to 20 by Figma: the chips are 36 tall (6346:46390)
	letter-spacing: -0.14px;

	border-radius: var(--size-12);
	color: var(--kova-lp-text);
	background: var(--kova-lp-surface);
	// Inset shadow rather than a border: Figma strokes sit inside the box, so a real
	// border would add 2px to every chip and drift the list.
	box-shadow: inset 0 0 0 1px var(--kova-lp-hairline-soft);
}

// Figma stores icon-checkmark upside-down and icon-close as a plus, then flips /
// rotates them through wrapper transforms — mirrored here so the vectors stay untouched.
// The vector is 11.81 wide inside a 14px icon box; the margin restores the box.
.kova-lp-usa__chip-check {
	flex-shrink: 0;
	width: 11.81px;
	margin-inline: 1.09px;

	transform: scaleY(-1);
}

.kova-lp-usa__actions {
	position: relative;

	display: flex;
	flex-direction: column-reverse; // mobile puts the promises above the CTA
	align-items: center;
	gap: var(--size-16);
	margin-top: var(--size-24);
}

.kova-lp-usa__promises {
	display: flex;
	justify-content: center;
	gap: var(--size-12);
}

.kova-lp-usa__promise {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	margin: 0;

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.14px;

	color: var(--kova-lp-text);
	white-space: nowrap;
}

.kova-lp-usa__promise-ring {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	padding: 1px;

	border: 1px solid currentColor;
	border-radius: var(--size-48);
}

.kova-lp-usa__cross {
	width: var(--size-12);
	height: var(--size-12);

	transform: scaleY(-1) rotate(45deg);
}

// 13.5 of vector in the 16px icon box (6346:46412).
.kova-lp-usa__promise-check {
	flex-shrink: 0;
	width: 13.5px;
	margin-inline: 1.25px;

	transform: scaleY(-1);
}

// The frame sets this button in Inter (Web/Button, 6346:46415); the desktop one is Poppins.
.kova-lp-usa__cta {
	box-sizing: border-box;

	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--size-4);
	width: 100%;
	height: var(--size-52);
	padding: var(--size-16) var(--size-12);

	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-18);

	border: none;
	border-radius: var(--size-16);
	color: var(--Fill-White);
	background: var(--kova-lp-brand);
	cursor: pointer;
}

.kova-lp-usa__cta-arrow {
	flex-shrink: 0;
	width: var(--size-20);
	height: var(--size-20);
}

// The row follows the panel on the page colour; the dot is set larger than the labels and
// is what gives it its height (mobile frame: 128 = 40 + 48 + 40). Mobile draws no fade.
.kova-lp-usa__marquee {
	padding-block: var(--size-40);

	font-family: var(--kova-lp-font);

	--kova-lp-marquee-gap: var(--size-20);
	--kova-lp-marquee-fade: 0px;
}

.kova-lp-usa__trust {
	display: flex;
	align-items: center;
	gap: var(--size-6);

	font-size: var(--size-24);
	font-weight: 500;
	line-height: 1.4;

	color: var(--kova-lp-muted);
	white-space: nowrap;
}

.kova-lp-usa__trust-icon {
	width: var(--size-42);
	height: var(--size-42);
}

.kova-lp-usa__trust-dot {
	font-size: var(--size-34);
	font-weight: 500;
	line-height: var(--size-48);

	color: var(--kova-lp-muted);
}

// Desktop (6202:57891) — the header bar and a two-column hero on one white surface: copy and
// CTA on the left, the product card on the right. 1024px rather than the repo-wide 768px, as
// for the other first screens: the columns collide below ~1280px of content.
//
// The frame paints the header on the page colour under a white halo (Rectangle 4253679); the
// header here takes the hero's white instead, so the first screen reads as one block.
@media (min-width: 1024px) {
	.kova-lp-usa {
		overflow: visible;
		padding: 0;

		border-radius: 0;
	}

	.kova-lp-usa__flag {
		display: none;
	}

	.kova-lp-usa__bar {
		box-sizing: border-box;
		justify-content: space-between;
		max-width: 1440px;
		height: auto;
		margin-inline: auto;
		padding: var(--size-24) 46px var(--size-32);
	}

	.kova-lp-usa__logo {
		width: 190px;
		height: var(--size-52);
	}

	.kova-lp-usa__bar-cta {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--size-20) var(--size-42);

		font-family: inherit;
		font-size: var(--size-20);
		font-weight: 500;
		line-height: normal;

		border: none;
		border-radius: var(--size-24);
		color: var(--Fill-White);
		background: var(--kova-lp-brand);
		cursor: pointer;
		white-space: nowrap;
	}

	.kova-lp-usa__inner {
		box-sizing: border-box;
		max-width: 1440px;
		margin-inline: auto;
		// The 758px band leaves 62px under its 696px columns (6202:57913).
		padding: 0 var(--size-80) 62px;

		// 582 / 674 are the frame's two columns; as a ratio they hold their proportion while
		// the 80px gutters and the 24px gap stay put. The card spans both rows and stretches
		// to the column; the copy row hugs and the actions row takes the rest.
		display: grid;
		grid-template-columns: minmax(0, 582fr) minmax(0, 674fr);
		grid-template-rows: max-content 1fr;
		column-gap: var(--size-24);
		row-gap: var(--size-40);
	}

	.kova-lp-usa__copy {
		grid-column: 1;
		grid-row: 1;
		gap: var(--size-24);
		margin-top: 0;
	}

	.kova-lp-usa__title {
		order: 0;
		max-width: none;
		margin-inline: 0;

		font-size: var(--size-52);
		font-weight: 500;
		line-height: 58px; // 1.12 on whole pixels: 3 x 58 = the 174px node (6202:57932)
		letter-spacing: -1.04px;

		text-align: left;
	}

	.kova-lp-usa__rating {
		margin-top: 0;
		padding: 0;
	}

	// One row at 1440 (513 of the 582 column); a narrower column moves the caption under the
	// score instead of pushing the pill under the card.
	.kova-lp-usa__rating-pill {
		align-self: start;
		width: fit-content;
		max-width: 100%;

		flex-direction: row;
		flex-wrap: wrap;
		align-items: start;
		gap: 0 var(--size-8);
		padding: var(--size-12) var(--size-16);

		--kova-lp-stars-tail-opacity: 0.4;
	}

	.kova-lp-usa__stars {
		height: var(--size-16);
	}

	.kova-lp-usa__rating-value,
	.kova-lp-usa__rating-caption {
		font-size: var(--size-16);
		line-height: var(--size-22); // 1.4, rounded to 22: the pill is 46 tall (6202:57917)
	}

	.kova-lp-usa__badge_place_rating {
		display: none;
	}

	.kova-lp-usa__description {
		margin-top: 0;

		font-size: var(--size-24);
		font-weight: 500;
		line-height: var(--size-32); // 1.32 on whole pixels: 3 x 32 = the 96px node (6327:41079)
		letter-spacing: normal;
	}

	// The price closes the copy at 28px, the column's other gaps are 24 (6202:57915).
	.kova-lp-usa__price_place_copy {
		display: flex;
		gap: var(--size-12);
		margin-top: var(--size-4);
	}

	.kova-lp-usa__price-label {
		font-size: var(--size-24);
		// 1.1 on whole pixels (6327:40413). Literal: --size-26 is 1.715rem (27.44px) in _sizes.scss.
		line-height: 26px;
		letter-spacing: -0.48px;

		color: var(--Text-Primary-60);
	}

	.kova-lp-usa__price-row {
		align-items: end;
		gap: var(--size-16);
		padding-block: 0;
	}

	.kova-lp-usa__price-now {
		padding-block: var(--size-4);

		font-size: 92px; // 6327:40416 — between --size-88 and --size-96, kept exact
		line-height: var(--size-64); // cap height at 92px — see the mobile rule
		letter-spacing: -1.84px;
	}

	.kova-lp-usa__price-aside {
		flex-shrink: 0;
		width: 124px;
		gap: var(--size-4);
	}

	.kova-lp-usa__price-was {
		font-size: var(--size-34);
		line-height: var(--size-48); // 1.4 on whole pixels (6327:40418)
		letter-spacing: normal;

		color: var(--kova-lp-muted);
	}

	.kova-lp-usa__price-period {
		font-size: var(--size-24);
		line-height: 26px; // as the label
		letter-spacing: -0.48px;
	}

	// section 1 (6202:57954). The shot sits 16px over the chips and both hug the bottom, so
	// extra chip rows on a narrower card push the shot up instead of under it.
	.kova-lp-usa__card {
		grid-column: 2;
		grid-row: 1 / span 2;
		overflow: hidden;

		justify-content: end;
		align-items: center;
		gap: 15.8px; // shot bottom 197.8 vs chips top 182 from the card bottom
		margin-top: 0;
		padding: var(--size-32);

		border-radius: var(--size-36);
	}

	.kova-lp-usa__badge_place_card {
		position: absolute;
		display: block;
		right: var(--size-32);
		top: var(--size-32);
		width: 160px; // 6327:40393
		height: 160px;
	}

	// Group 2087328215 (6327:44941) — 439px at 1440; narrower cards shrink it to their width.
	.kova-lp-usa__shot {
		display: block;
		width: 439px;
		max-width: 100%;
		height: auto;
	}

	.kova-lp-usa__product {
		display: none;
	}

	.kova-lp-usa__chips {
		align-self: stretch;
		gap: var(--size-6);
	}

	.kova-lp-usa__chip {
		padding: var(--size-12) var(--size-14);

		font-size: var(--size-16);
		line-height: var(--size-22); // 1.4, rounded to 22: the chips are 46 tall (6202:57959)
		letter-spacing: -0.16px;

		border-radius: var(--size-14);
	}

	.kova-lp-usa__actions {
		grid-column: 1;
		grid-row: 2;
		flex-direction: column; // desktop puts the CTA above the promises
		align-items: start;
		gap: var(--size-32);
		margin-top: 0;
	}

	.kova-lp-usa__cta {
		gap: var(--size-8);
		width: auto;
		max-width: 100%;
		height: 90px; // 6202:57943 — between --size-88 and --size-96, kept exact
		// 60px at 1440 (6202:57943); the narrowest desktop column cannot fit the label with
		// that, so the sides give way before the label wraps.
		padding: 0 min(var(--size-60), 4.2vw);

		font-family: inherit;
		font-size: var(--size-24);
		font-weight: 500;
		line-height: normal;
		white-space: nowrap;

		border-radius: var(--size-28);
	}

	.kova-lp-usa__cta-arrow {
		width: var(--size-32);
		height: var(--size-32);
	}

	.kova-lp-usa__promises {
		flex-wrap: wrap;
		gap: var(--size-16);
	}

	.kova-lp-usa__promise {
		gap: var(--size-8);

		font-size: var(--size-18);
		font-weight: 400;
		line-height: 26px; // 1.44 on whole pixels (6202:57950); --size-26 is off, see the price label
		letter-spacing: normal;
	}

	// The second promise sits 4px from its icon while the first sits 8px (6202:57946).
	.kova-lp-usa__promise:last-child {
		gap: var(--size-4);
	}

	.kova-lp-usa__promise-ring {
		width: var(--size-24);
		height: var(--size-24);
		padding: var(--size-2);

		border-width: var(--size-2);
		border-radius: var(--size-80);
	}

	.kova-lp-usa__cross {
		width: var(--size-18);
		height: var(--size-18);
	}

	// 20.25 of vector in the 24px icon box (6202:57952).
	.kova-lp-usa__promise-check {
		width: 20.25px;
		margin-inline: 1.875px;
	}

	// lines (6202:57994) — same ticker, one size up, faded into the page from 60px in:
	// the frame's 300px "light" overlays hold the page colour solid for their first 20%.
	.kova-lp-usa__marquee {
		padding-block: var(--size-36);

		mask-image: linear-gradient(
			to right,
			transparent var(--size-60),
			#000 300px,
			#000 calc(100% - 300px),
			transparent calc(100% - var(--size-60))
		);

		--kova-lp-marquee-gap: var(--size-28);
	}

	.kova-lp-usa__trust {
		font-size: var(--size-34);
	}
}
</style>
